import { defineMessages } from './define'

/* Sign-in (phone, email, one-time code) and user management. */

export default defineMessages({
  en: {
    // sign-in
    'access.identifier': 'Phone number or email',
    'access.identifierPh': '98765 43210 or name@example.com',
    'access.usernameStillWorks': 'Staff with an older login can still use their username.',
    'access.howToSignIn': 'How to sign in',
    'access.oneTimeCode': 'One-time code',
    'access.sendCode': 'Send code',
    'access.sending': 'Sending…',
    'access.couldNotSend': 'Could not send a code.',
    'access.codeSent': 'If {to} is registered, a 6-digit code has been sent to it. It expires in {min} minutes.',
    'access.devCode': 'Development server: your code is {code}.',
    'access.codeLabel': '6-digit code',
    'access.verifyAndSignIn': 'Verify and sign in',
    'access.changeIdentifier': 'Use a different number',
    'access.resend': 'Send a new code',
    'access.resendIn': 'New code in {s} s',
    'access.sessionExpired': 'Your session ended. Sign in again to carry on.',
    'access.signedOut': 'You have signed out.',
    'access.vIdentifier': 'Enter your phone number or email.',
    'access.vPhone': 'Enter a valid 10-digit mobile number starting 6, 7, 8 or 9.',
    'access.vEmail': 'Enter a valid email address, like name@example.com.',
    'access.vPhoneOrEmail': 'Codes are sent to a phone number or email, not a username.',
    'access.vIdentifierShape': 'Enter a valid phone number or email address.',
    'access.vPasswordShort': 'Use at least 8 characters.',
    'access.vPasswordLong': 'Use at most 128 characters.',
    'access.vPasswordDigits': 'Use letters too, not only numbers.',
    'access.myDetails': 'My details',
    'access.contactDetails': 'Contact details',
    'access.contactNote': 'Keep these up to date so the clinic can reach you.',
    'access.emergencyContact': 'Emergency contact',
    'access.emergencyHint': 'Someone the clinic can call if they cannot reach you.',
    'access.occupation': 'Occupation',
    'access.medicalRecord': 'Medical record',
    'access.medicalRecordNote': 'These are part of your medical record, so only the clinic can change them. Ask at the desk if anything is wrong.',
    'access.detailsSaved': 'Your details have been saved.',
    'access.couldNotLoadDetails': 'Could not load your details.',
    'access.changePhone': 'Change sign-in number',
    'access.currentPhone': 'Sign-in number',
    'access.noPhoneYet': 'None yet',
    'access.newPhone': 'New mobile number',
    'access.changePhoneNote': 'We send a code to the new number. Your number changes only after you enter that code here, and your old number is told about the change.',
    'access.phoneCodeSent': 'If {to} can receive messages, a 6-digit code is on its way. It expires in {min} minutes.',
    'access.confirmNewPhone': 'Confirm new number',
    'access.phoneChanged': 'Your sign-in number has been changed.',
    'access.act.phone_changed': 'Sign-in number changed',
    'access.act.otp_phone': 'Code sent to confirm a new number',
    // user management
    'access.users': 'Users',
    'access.addUser': 'Add user',
    'access.newUser': 'New user',
    'access.editUser': 'Edit user',
    'access.userDetails': 'User',
    'access.searchUsers': 'Search name, phone, email or username',
    'access.filterUsers': 'Filter users',
    'access.allRoles': 'All roles',
    'access.anyStatus': 'Any status',
    'access.status': 'Status',
    'access.noUsersMatch': 'No users match these filters.',
    'access.couldNotLoad': 'Could not load users.',
    'access.mobile': 'Mobile number',
    'access.email': 'Email',
    'access.signInHint': 'They can sign in with this phone number or email.',
    'access.linkedPatient': 'Linked patient',
    'access.findPatient': 'Search patient by name, phone or code',
    'access.change': 'Change',
    'access.vUsername': 'At least 3 letters or numbers; dots, dashes and underscores are fine.',
    'access.vDoctor': 'Choose the doctor this login belongs to.',
    'access.vPatient': 'Choose the patient this login belongs to.',
    'access.cannotChangeOwnRole': 'You cannot change your own role. Another administrator has to.',
    'access.roleChangeNote': 'Changing the role signs them out everywhere, so the new permissions apply at once.',
    'access.roleChanged': 'Role changed to {role}.',
    'access.saved': 'Saved.',
    'access.clinic': 'Clinic',
    'access.created': 'Created',
    'access.lastLogin': 'Last sign-in',
    'access.never': 'Never',
    'access.sessions': 'Signed in on',
    'access.sessionsCount': '{n} device(s)',
    'access.selfNote': 'This is your own account. You can edit your details, but not your role or status.',
    'access.signOutEverywhere': 'Sign out everywhere',
    'access.signOutEverywhereNote': 'Ends every session {name} has open, on every device. Nothing else changes.',
    'access.signedOutEverywhere': '{name} has been signed out everywhere.',
    'access.deactivateNote': '{name} will be signed out everywhere and will not be able to sign in. Their records stay.',
    'access.reactivateNote': '{name} will be able to sign in again.',
    'access.activity': 'Activity',
    'access.noActivity': 'Nothing recorded yet.',
    'access.by': 'by {name}',
    'access.onAnotherAccount': 'on another account',
    'access.act.create': 'Account created',
    'access.act.update': 'Details changed',
    'access.act.role_change': 'Role changed',
    'access.act.activate': 'Activated',
    'access.act.deactivate': 'Deactivated',
    'access.act.login': 'Signed in',
    'access.act.logout': 'Signed out',
    'access.act.login_failed': 'Failed sign-in',
    'access.act.otp_sent': 'Sign-in code sent',
    'access.act.password_reset': 'Password reset',
  },
  hi: {
    'access.identifier': 'फ़ोन नंबर या ईमेल',
    'access.identifierPh': '98765 43210 या name@example.com',
    'access.usernameStillWorks': 'पुराने लॉगिन वाले स्टाफ़ अपना यूज़रनेम भी इस्तेमाल कर सकते हैं।',
    'access.howToSignIn': 'साइन इन कैसे करें',
    'access.oneTimeCode': 'वन-टाइम कोड',
    'access.sendCode': 'कोड भेजें',
    'access.sending': 'भेजा जा रहा है…',
    'access.couldNotSend': 'कोड नहीं भेजा जा सका।',
    'access.codeSent': 'अगर {to} पंजीकृत है, तो उस पर 6 अंकों का कोड भेजा गया है। यह {min} मिनट में समाप्त होगा।',
    'access.devCode': 'डेवलपमेंट सर्वर: आपका कोड {code} है।',
    'access.codeLabel': '6 अंकों का कोड',
    'access.verifyAndSignIn': 'जाँचें और साइन इन करें',
    'access.changeIdentifier': 'दूसरा नंबर इस्तेमाल करें',
    'access.resend': 'नया कोड भेजें',
    'access.resendIn': '{s} सेकंड में नया कोड',
    'access.sessionExpired': 'आपका सत्र समाप्त हो गया। जारी रखने के लिए फिर से साइन इन करें।',
    'access.signedOut': 'आप साइन आउट हो गए हैं।',
    'access.vIdentifier': 'अपना फ़ोन नंबर या ईमेल दर्ज करें।',
    'access.vPhone': '6, 7, 8 या 9 से शुरू होने वाला सही 10 अंकों का मोबाइल नंबर दर्ज करें।',
    'access.vEmail': 'सही ईमेल पता दर्ज करें, जैसे name@example.com।',
    'access.vPhoneOrEmail': 'कोड फ़ोन नंबर या ईमेल पर भेजे जाते हैं, यूज़रनेम पर नहीं।',
    'access.vIdentifierShape': 'सही फ़ोन नंबर या ईमेल पता दर्ज करें।',
    'access.vPasswordShort': 'कम से कम 8 अक्षर रखें।',
    'access.vPasswordLong': 'अधिकतम 128 अक्षर रखें।',
    'access.vPasswordDigits': 'सिर्फ़ अंक नहीं, अक्षर भी रखें।',
    'access.myDetails': 'मेरा विवरण',
    'access.contactDetails': 'संपर्क विवरण',
    'access.contactNote': 'इन्हें अपडेट रखें ताकि क्लिनिक आपसे संपर्क कर सके।',
    'access.emergencyContact': 'आपातकालीन संपर्क',
    'access.emergencyHint': 'कोई व्यक्ति जिसे क्लिनिक आपसे संपर्क न होने पर फ़ोन कर सके।',
    'access.occupation': 'पेशा',
    'access.medicalRecord': 'मेडिकल रिकॉर्ड',
    'access.medicalRecordNote': 'ये आपके मेडिकल रिकॉर्ड का हिस्सा हैं, इसलिए इन्हें सिर्फ़ क्लिनिक बदल सकता है। कुछ गलत हो तो रिसेप्शन पर बताएँ।',
    'access.detailsSaved': 'आपका विवरण सहेज दिया गया है।',
    'access.couldNotLoadDetails': 'आपका विवरण लोड नहीं हो सका।',
    'access.changePhone': 'साइन-इन नंबर बदलें',
    'access.currentPhone': 'साइन-इन नंबर',
    'access.noPhoneYet': 'अभी कोई नहीं',
    'access.newPhone': 'नया मोबाइल नंबर',
    'access.changePhoneNote': 'हम नए नंबर पर एक कोड भेजेंगे। वह कोड यहाँ डालने के बाद ही आपका नंबर बदलेगा, और आपके पुराने नंबर पर इसकी सूचना जाएगी।',
    'access.phoneCodeSent': 'अगर {to} पर संदेश आते हैं, तो 6 अंकों का कोड भेजा जा रहा है। यह {min} मिनट में समाप्त होगा।',
    'access.confirmNewPhone': 'नया नंबर पक्का करें',
    'access.phoneChanged': 'आपका साइन-इन नंबर बदल दिया गया है।',
    'access.act.phone_changed': 'साइन-इन नंबर बदला गया',
    'access.act.otp_phone': 'नए नंबर की पुष्टि के लिए कोड भेजा गया',
    'access.users': 'उपयोगकर्ता',
    'access.addUser': 'उपयोगकर्ता जोड़ें',
    'access.newUser': 'नया उपयोगकर्ता',
    'access.editUser': 'उपयोगकर्ता बदलें',
    'access.userDetails': 'उपयोगकर्ता',
    'access.searchUsers': 'नाम, फ़ोन, ईमेल या यूज़रनेम से खोजें',
    'access.filterUsers': 'उपयोगकर्ता फ़िल्टर करें',
    'access.allRoles': 'सभी भूमिकाएँ',
    'access.anyStatus': 'कोई भी स्थिति',
    'access.status': 'स्थिति',
    'access.noUsersMatch': 'इन फ़िल्टर से कोई उपयोगकर्ता नहीं मिला।',
    'access.couldNotLoad': 'उपयोगकर्ता लोड नहीं हो सके।',
    'access.mobile': 'मोबाइल नंबर',
    'access.email': 'ईमेल',
    'access.signInHint': 'वे इस फ़ोन नंबर या ईमेल से साइन इन कर सकते हैं।',
    'access.linkedPatient': 'जुड़ा हुआ मरीज़',
    'access.findPatient': 'नाम, फ़ोन या कोड से मरीज़ खोजें',
    'access.change': 'बदलें',
    'access.vUsername': 'कम से कम 3 अक्षर या अंक; बिंदु, डैश और अंडरस्कोर चल सकते हैं।',
    'access.vDoctor': 'चुनें कि यह लॉगिन किस डॉक्टर का है।',
    'access.vPatient': 'चुनें कि यह लॉगिन किस मरीज़ का है।',
    'access.cannotChangeOwnRole': 'आप अपनी भूमिका खुद नहीं बदल सकते। किसी दूसरे एडमिन को बदलनी होगी।',
    'access.roleChangeNote': 'भूमिका बदलने पर वे हर जगह से साइन आउट हो जाएँगे, ताकि नई अनुमतियाँ तुरंत लागू हों।',
    'access.roleChanged': 'भूमिका {role} कर दी गई।',
    'access.saved': 'सहेजा गया।',
    'access.clinic': 'क्लिनिक',
    'access.created': 'बनाया गया',
    'access.lastLogin': 'पिछला साइन इन',
    'access.never': 'कभी नहीं',
    'access.sessions': 'साइन इन हैं',
    'access.sessionsCount': '{n} डिवाइस',
    'access.selfNote': 'यह आपका अपना खाता है। आप अपना विवरण बदल सकते हैं, पर अपनी भूमिका या स्थिति नहीं।',
    'access.signOutEverywhere': 'हर जगह से साइन आउट करें',
    'access.signOutEverywhereNote': '{name} के सभी डिवाइस पर खुले सत्र बंद हो जाएँगे। और कुछ नहीं बदलेगा।',
    'access.signedOutEverywhere': '{name} को हर जगह से साइन आउट कर दिया गया।',
    'access.deactivateNote': '{name} हर जगह से साइन आउट हो जाएँगे और साइन इन नहीं कर पाएँगे। उनके रिकॉर्ड बने रहेंगे।',
    'access.reactivateNote': '{name} फिर से साइन इन कर पाएँगे।',
    'access.activity': 'गतिविधि',
    'access.noActivity': 'अभी कुछ दर्ज नहीं है।',
    'access.by': '{name} द्वारा',
    'access.onAnotherAccount': 'दूसरे खाते पर',
    'access.act.create': 'खाता बनाया गया',
    'access.act.update': 'विवरण बदला गया',
    'access.act.role_change': 'भूमिका बदली गई',
    'access.act.activate': 'सक्रिय किया गया',
    'access.act.deactivate': 'निष्क्रिय किया गया',
    'access.act.login': 'साइन इन किया',
    'access.act.logout': 'साइन आउट किया',
    'access.act.login_failed': 'साइन इन विफल',
    'access.act.otp_sent': 'साइन-इन कोड भेजा गया',
    'access.act.password_reset': 'पासवर्ड रीसेट',
  },
  mr: {
    'access.identifier': 'फोन नंबर किंवा ईमेल',
    'access.identifierPh': '98765 43210 किंवा name@example.com',
    'access.usernameStillWorks': 'जुन्या लॉगिनचे कर्मचारी त्यांचे यूजरनेमही वापरू शकतात.',
    'access.howToSignIn': 'साइन इन कसे करायचे',
    'access.oneTimeCode': 'वन-टाइम कोड',
    'access.sendCode': 'कोड पाठवा',
    'access.sending': 'पाठवत आहे…',
    'access.couldNotSend': 'कोड पाठवता आला नाही.',
    'access.codeSent': '{to} नोंदणीकृत असल्यास त्यावर 6 अंकी कोड पाठवला आहे. तो {min} मिनिटांत संपेल.',
    'access.devCode': 'डेव्हलपमेंट सर्व्हर: तुमचा कोड {code} आहे.',
    'access.codeLabel': '6 अंकी कोड',
    'access.verifyAndSignIn': 'तपासा आणि साइन इन करा',
    'access.changeIdentifier': 'दुसरा नंबर वापरा',
    'access.resend': 'नवीन कोड पाठवा',
    'access.resendIn': '{s} सेकंदांत नवीन कोड',
    'access.sessionExpired': 'तुमचे सत्र संपले. पुढे जाण्यासाठी पुन्हा साइन इन करा.',
    'access.signedOut': 'तुम्ही साइन आउट झाला आहात.',
    'access.vIdentifier': 'तुमचा फोन नंबर किंवा ईमेल टाका.',
    'access.vPhone': '6, 7, 8 किंवा 9 ने सुरू होणारा योग्य 10 अंकी मोबाइल नंबर टाका.',
    'access.vEmail': 'योग्य ईमेल पत्ता टाका, उदा. name@example.com.',
    'access.vPhoneOrEmail': 'कोड फोन नंबर किंवा ईमेलवर पाठवले जातात, यूजरनेमवर नाही.',
    'access.vIdentifierShape': 'योग्य फोन नंबर किंवा ईमेल पत्ता टाका.',
    'access.vPasswordShort': 'किमान 8 अक्षरे वापरा.',
    'access.vPasswordLong': 'जास्तीत जास्त 128 अक्षरे वापरा.',
    'access.vPasswordDigits': 'फक्त अंक नको, अक्षरेही वापरा.',
    'access.myDetails': 'माझी माहिती',
    'access.contactDetails': 'संपर्क माहिती',
    'access.contactNote': 'क्लिनिकला तुमच्याशी संपर्क करता यावा म्हणून ही माहिती अद्ययावत ठेवा.',
    'access.emergencyContact': 'आपत्कालीन संपर्क',
    'access.emergencyHint': 'तुमच्याशी संपर्क न झाल्यास क्लिनिक ज्यांना फोन करू शकेल अशी व्यक्ती.',
    'access.occupation': 'व्यवसाय',
    'access.medicalRecord': 'वैद्यकीय नोंद',
    'access.medicalRecordNote': 'ही तुमच्या वैद्यकीय नोंदीचा भाग आहे, म्हणून फक्त क्लिनिक ती बदलू शकते. काही चुकीचे असल्यास रिसेप्शनवर सांगा.',
    'access.detailsSaved': 'तुमची माहिती जतन केली आहे.',
    'access.couldNotLoadDetails': 'तुमची माहिती लोड करता आली नाही.',
    'access.changePhone': 'साइन-इन नंबर बदला',
    'access.currentPhone': 'साइन-इन नंबर',
    'access.noPhoneYet': 'अजून नाही',
    'access.newPhone': 'नवीन मोबाइल नंबर',
    'access.changePhoneNote': 'आम्ही नवीन नंबरवर कोड पाठवू. तो कोड इथे टाकल्यावरच तुमचा नंबर बदलेल, आणि जुन्या नंबरवर त्याची सूचना जाईल.',
    'access.phoneCodeSent': '{to} वर संदेश येत असल्यास 6 अंकी कोड पाठवला जात आहे. तो {min} मिनिटांत संपेल.',
    'access.confirmNewPhone': 'नवीन नंबर निश्चित करा',
    'access.phoneChanged': 'तुमचा साइन-इन नंबर बदलला आहे.',
    'access.act.phone_changed': 'साइन-इन नंबर बदलला',
    'access.act.otp_phone': 'नवीन नंबरच्या पुष्टीसाठी कोड पाठवला',
    'access.users': 'वापरकर्ते',
    'access.addUser': 'वापरकर्ता जोडा',
    'access.newUser': 'नवीन वापरकर्ता',
    'access.editUser': 'वापरकर्ता बदला',
    'access.userDetails': 'वापरकर्ता',
    'access.searchUsers': 'नाव, फोन, ईमेल किंवा यूजरनेमने शोधा',
    'access.filterUsers': 'वापरकर्ते फिल्टर करा',
    'access.allRoles': 'सर्व भूमिका',
    'access.anyStatus': 'कोणतीही स्थिती',
    'access.status': 'स्थिती',
    'access.noUsersMatch': 'या फिल्टरनुसार कोणी वापरकर्ता सापडला नाही.',
    'access.couldNotLoad': 'वापरकर्ते लोड करता आले नाहीत.',
    'access.mobile': 'मोबाइल नंबर',
    'access.email': 'ईमेल',
    'access.signInHint': 'ते या फोन नंबर किंवा ईमेलने साइन इन करू शकतात.',
    'access.linkedPatient': 'जोडलेला रुग्ण',
    'access.findPatient': 'नाव, फोन किंवा कोडने रुग्ण शोधा',
    'access.change': 'बदला',
    'access.vUsername': 'किमान 3 अक्षरे किंवा अंक; बिंदू, डॅश आणि अंडरस्कोर चालतात.',
    'access.vDoctor': 'हे लॉगिन कोणत्या डॉक्टरचे आहे ते निवडा.',
    'access.vPatient': 'हे लॉगिन कोणत्या रुग्णाचे आहे ते निवडा.',
    'access.cannotChangeOwnRole': 'तुम्ही स्वतःची भूमिका बदलू शकत नाही. दुसऱ्या अ‍ॅडमिनने बदलायला हवी.',
    'access.roleChangeNote': 'भूमिका बदलल्यावर ते सगळीकडून साइन आउट होतील, म्हणजे नवीन परवानग्या लगेच लागू होतील.',
    'access.roleChanged': 'भूमिका {role} केली.',
    'access.saved': 'जतन केले.',
    'access.clinic': 'क्लिनिक',
    'access.created': 'तयार केले',
    'access.lastLogin': 'शेवटचे साइन इन',
    'access.never': 'कधीही नाही',
    'access.sessions': 'साइन इन आहे',
    'access.sessionsCount': '{n} डिव्हाइस',
    'access.selfNote': 'हे तुमचे स्वतःचे खाते आहे. तुम्ही तपशील बदलू शकता, पण भूमिका किंवा स्थिती नाही.',
    'access.signOutEverywhere': 'सगळीकडून साइन आउट करा',
    'access.signOutEverywhereNote': '{name} यांची सर्व डिव्हाइसवरील उघडी सत्रे बंद होतील. बाकी काही बदलणार नाही.',
    'access.signedOutEverywhere': '{name} यांना सगळीकडून साइन आउट केले.',
    'access.deactivateNote': '{name} सगळीकडून साइन आउट होतील आणि साइन इन करू शकणार नाहीत. त्यांच्या नोंदी राहतील.',
    'access.reactivateNote': '{name} पुन्हा साइन इन करू शकतील.',
    'access.activity': 'हालचाली',
    'access.noActivity': 'अजून काही नोंदवलेले नाही.',
    'access.by': '{name} यांनी',
    'access.onAnotherAccount': 'दुसऱ्या खात्यावर',
    'access.act.create': 'खाते तयार केले',
    'access.act.update': 'तपशील बदलले',
    'access.act.role_change': 'भूमिका बदलली',
    'access.act.activate': 'सक्रिय केले',
    'access.act.deactivate': 'निष्क्रिय केले',
    'access.act.login': 'साइन इन केले',
    'access.act.logout': 'साइन आउट केले',
    'access.act.login_failed': 'साइन इन अयशस्वी',
    'access.act.otp_sent': 'साइन-इन कोड पाठवला',
    'access.act.password_reset': 'पासवर्ड रीसेट',
  },
  or: {
    'access.identifier': 'ଫୋନ୍ ନମ୍ବର କିମ୍ବା ଇମେଲ୍',
    'access.identifierPh': '98765 43210 କିମ୍ବା name@example.com',
    'access.usernameStillWorks': 'ପୁରୁଣା ଲଗଇନ୍ ଥିବା କର୍ମଚାରୀ ସେମାନଙ୍କ ୟୁଜରନେମ୍ ମଧ୍ୟ ବ୍ୟବହାର କରିପାରିବେ।',
    'access.howToSignIn': 'କିପରି ସାଇନ୍ ଇନ୍ କରିବେ',
    'access.oneTimeCode': 'ୱାନ୍-ଟାଇମ୍ କୋଡ୍',
    'access.sendCode': 'କୋଡ୍ ପଠାନ୍ତୁ',
    'access.sending': 'ପଠାଯାଉଛି…',
    'access.couldNotSend': 'କୋଡ୍ ପଠାଯାଇପାରିଲା ନାହିଁ।',
    'access.codeSent': '{to} ପଞ୍ଜୀକୃତ ଥିଲେ ସେଥିରେ 6 ଅଙ୍କର କୋଡ୍ ପଠାଯାଇଛି। ଏହା {min} ମିନିଟରେ ଶେଷ ହେବ।',
    'access.devCode': 'ଡେଭେଲପମେଣ୍ଟ ସର୍ଭର: ଆପଣଙ୍କ କୋଡ୍ {code}।',
    'access.codeLabel': '6 ଅଙ୍କର କୋଡ୍',
    'access.verifyAndSignIn': 'ଯାଞ୍ଚ କରି ସାଇନ୍ ଇନ୍ କରନ୍ତୁ',
    'access.changeIdentifier': 'ଅନ୍ୟ ନମ୍ବର ବ୍ୟବହାର କରନ୍ତୁ',
    'access.resend': 'ନୂଆ କୋଡ୍ ପଠାନ୍ତୁ',
    'access.resendIn': '{s} ସେକେଣ୍ଡରେ ନୂଆ କୋଡ୍',
    'access.sessionExpired': 'ଆପଣଙ୍କ ସେସନ୍ ଶେଷ ହୋଇଗଲା। ଜାରି ରଖିବାକୁ ପୁଣି ସାଇନ୍ ଇନ୍ କରନ୍ତୁ।',
    'access.signedOut': 'ଆପଣ ସାଇନ୍ ଆଉଟ୍ ହୋଇଛନ୍ତି।',
    'access.vIdentifier': 'ଆପଣଙ୍କ ଫୋନ୍ ନମ୍ବର କିମ୍ବା ଇମେଲ୍ ଦିଅନ୍ତୁ।',
    'access.vPhone': '6, 7, 8 କିମ୍ବା 9 ରୁ ଆରମ୍ଭ ହେଉଥିବା ଠିକ୍ 10 ଅଙ୍କର ମୋବାଇଲ୍ ନମ୍ବର ଦିଅନ୍ତୁ।',
    'access.vEmail': 'ଠିକ୍ ଇମେଲ୍ ଠିକଣା ଦିଅନ୍ତୁ, ଯେପରି name@example.com।',
    'access.vPhoneOrEmail': 'କୋଡ୍ ଫୋନ୍ ନମ୍ବର କିମ୍ବା ଇମେଲକୁ ପଠାଯାଏ, ୟୁଜରନେମକୁ ନୁହେଁ।',
    'access.vIdentifierShape': 'ଠିକ୍ ଫୋନ୍ ନମ୍ବର କିମ୍ବା ଇମେଲ୍ ଠିକଣା ଦିଅନ୍ତୁ।',
    'access.vPasswordShort': 'ଅତି କମରେ 8ଟି ଅକ୍ଷର ରଖନ୍ତୁ।',
    'access.vPasswordLong': 'ସର୍ବାଧିକ 128ଟି ଅକ୍ଷର ରଖନ୍ତୁ।',
    'access.vPasswordDigits': 'କେବଳ ଅଙ୍କ ନୁହେଁ, ଅକ୍ଷର ମଧ୍ୟ ରଖନ୍ତୁ।',
    'access.myDetails': 'ମୋ ବିବରଣୀ',
    'access.contactDetails': 'ଯୋଗାଯୋଗ ବିବରଣୀ',
    'access.contactNote': 'କ୍ଲିନିକ୍ ଆପଣଙ୍କ ସହ ଯୋଗାଯୋଗ କରିପାରିବା ପାଇଁ ଏଗୁଡ଼ିକୁ ଅପଡେଟ୍ ରଖନ୍ତୁ।',
    'access.emergencyContact': 'ଜରୁରୀ ଯୋଗାଯୋଗ',
    'access.emergencyHint': 'ଆପଣଙ୍କୁ ନ ପାଇଲେ କ୍ଲିନିକ୍ ଯାହାଙ୍କୁ ଫୋନ୍ କରିପାରିବ।',
    'access.occupation': 'ବୃତ୍ତି',
    'access.medicalRecord': 'ଚିକିତ୍ସା ରେକର୍ଡ',
    'access.medicalRecordNote': 'ଏଗୁଡ଼ିକ ଆପଣଙ୍କ ଚିକିତ୍ସା ରେକର୍ଡର ଅଂଶ, ତେଣୁ କେବଳ କ୍ଲିନିକ୍ ବଦଳାଇପାରିବ। କିଛି ଭୁଲ ଥିଲେ ରିସେପସନରେ କୁହନ୍ତୁ।',
    'access.detailsSaved': 'ଆପଣଙ୍କ ବିବରଣୀ ସେଭ୍ ହେଲା।',
    'access.couldNotLoadDetails': 'ଆପଣଙ୍କ ବିବରଣୀ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'access.changePhone': 'ସାଇନ୍-ଇନ୍ ନମ୍ବର ବଦଳାନ୍ତୁ',
    'access.currentPhone': 'ସାଇନ୍-ଇନ୍ ନମ୍ବର',
    'access.noPhoneYet': 'ଏପର୍ଯ୍ୟନ୍ତ ନାହିଁ',
    'access.newPhone': 'ନୂଆ ମୋବାଇଲ୍ ନମ୍ବର',
    'access.changePhoneNote': 'ଆମେ ନୂଆ ନମ୍ବରକୁ ଏକ କୋଡ୍ ପଠାଇବୁ। ସେହି କୋଡ୍ ଏଠାରେ ଦେବା ପରେ ହିଁ ଆପଣଙ୍କ ନମ୍ବର ବଦଳିବ, ଏବଂ ପୁରୁଣା ନମ୍ବରକୁ ସୂଚନା ଯିବ।',
    'access.phoneCodeSent': '{to} ରେ ମେସେଜ୍ ଆସୁଥିଲେ 6 ଅଙ୍କର କୋଡ୍ ପଠାଯାଉଛି। ଏହା {min} ମିନିଟରେ ଶେଷ ହେବ।',
    'access.confirmNewPhone': 'ନୂଆ ନମ୍ବର ନିଶ୍ଚିତ କରନ୍ତୁ',
    'access.phoneChanged': 'ଆପଣଙ୍କ ସାଇନ୍-ଇନ୍ ନମ୍ବର ବଦଳିଗଲା।',
    'access.act.phone_changed': 'ସାଇନ୍-ଇନ୍ ନମ୍ବର ବଦଳିଲା',
    'access.act.otp_phone': 'ନୂଆ ନମ୍ବର ନିଶ୍ଚିତ କରିବାକୁ କୋଡ୍ ପଠାଗଲା',
    'access.users': 'ବ୍ୟବହାରକାରୀ',
    'access.addUser': 'ବ୍ୟବହାରକାରୀ ଯୋଡନ୍ତୁ',
    'access.newUser': 'ନୂଆ ବ୍ୟବହାରକାରୀ',
    'access.editUser': 'ବ୍ୟବହାରକାରୀ ବଦଳାନ୍ତୁ',
    'access.userDetails': 'ବ୍ୟବହାରକାରୀ',
    'access.searchUsers': 'ନାମ, ଫୋନ୍, ଇମେଲ୍ କିମ୍ବା ୟୁଜରନେମ୍ ଦ୍ୱାରା ଖୋଜନ୍ତୁ',
    'access.filterUsers': 'ବ୍ୟବହାରକାରୀ ଫିଲ୍ଟର କରନ୍ତୁ',
    'access.allRoles': 'ସମସ୍ତ ଭୂମିକା',
    'access.anyStatus': 'ଯେକୌଣସି ସ୍ଥିତି',
    'access.status': 'ସ୍ଥିତି',
    'access.noUsersMatch': 'ଏହି ଫିଲ୍ଟରରେ କୌଣସି ବ୍ୟବହାରକାରୀ ମିଳିଲେ ନାହିଁ।',
    'access.couldNotLoad': 'ବ୍ୟବହାରକାରୀ ଲୋଡ୍ ହୋଇପାରିଲେ ନାହିଁ।',
    'access.mobile': 'ମୋବାଇଲ୍ ନମ୍ବର',
    'access.email': 'ଇମେଲ୍',
    'access.signInHint': 'ସେମାନେ ଏହି ଫୋନ୍ ନମ୍ବର କିମ୍ବା ଇମେଲ୍ ଦ୍ୱାରା ସାଇନ୍ ଇନ୍ କରିପାରିବେ।',
    'access.linkedPatient': 'ଯୋଡ଼ାଯାଇଥିବା ରୋଗୀ',
    'access.findPatient': 'ନାମ, ଫୋନ୍ କିମ୍ବା କୋଡ୍ ଦ୍ୱାରା ରୋଗୀ ଖୋଜନ୍ତୁ',
    'access.change': 'ବଦଳାନ୍ତୁ',
    'access.vUsername': 'ଅତି କମରେ 3ଟି ଅକ୍ଷର କିମ୍ବା ଅଙ୍କ; ବିନ୍ଦୁ, ଡ୍ୟାସ୍ ଓ ଅଣ୍ଡରସ୍କୋର ଚଳିବ।',
    'access.vDoctor': 'ଏହି ଲଗଇନ୍ କେଉଁ ଡାକ୍ତରଙ୍କର ତାହା ବାଛନ୍ତୁ।',
    'access.vPatient': 'ଏହି ଲଗଇନ୍ କେଉଁ ରୋଗୀଙ୍କର ତାହା ବାଛନ୍ତୁ।',
    'access.cannotChangeOwnRole': 'ଆପଣ ନିଜ ଭୂମିକା ବଦଳାଇ ପାରିବେ ନାହିଁ। ଅନ୍ୟ ଜଣେ ଆଡମିନଙ୍କୁ ବଦଳାଇବାକୁ ପଡ଼ିବ।',
    'access.roleChangeNote': 'ଭୂମିକା ବଦଳିଲେ ସେମାନେ ସବୁଠାରୁ ସାଇନ୍ ଆଉଟ୍ ହେବେ, ଯାହାଫଳରେ ନୂଆ ଅନୁମତି ସଙ୍ଗେ ସଙ୍ଗେ ଲାଗୁ ହେବ।',
    'access.roleChanged': 'ଭୂମିକା {role} କରାଗଲା।',
    'access.saved': 'ସେଭ୍ ହେଲା।',
    'access.clinic': 'କ୍ଲିନିକ୍',
    'access.created': 'ସୃଷ୍ଟି ହେଲା',
    'access.lastLogin': 'ଶେଷ ସାଇନ୍ ଇନ୍',
    'access.never': 'କେବେ ନୁହେଁ',
    'access.sessions': 'ସାଇନ୍ ଇନ୍ ଅଛି',
    'access.sessionsCount': '{n}ଟି ଡିଭାଇସ୍',
    'access.selfNote': 'ଏହା ଆପଣଙ୍କ ନିଜ ଖାତା। ଆପଣ ବିବରଣୀ ବଦଳାଇ ପାରିବେ, କିନ୍ତୁ ଭୂମିକା କିମ୍ବା ସ୍ଥିତି ନୁହେଁ।',
    'access.signOutEverywhere': 'ସବୁଠାରୁ ସାଇନ୍ ଆଉଟ୍ କରନ୍ତୁ',
    'access.signOutEverywhereNote': '{name}ଙ୍କ ସମସ୍ତ ଡିଭାଇସରେ ଖୋଲା ସେସନ୍ ବନ୍ଦ ହେବ। ଆଉ କିଛି ବଦଳିବ ନାହିଁ।',
    'access.signedOutEverywhere': '{name}ଙ୍କୁ ସବୁଠାରୁ ସାଇନ୍ ଆଉଟ୍ କରାଗଲା।',
    'access.deactivateNote': '{name} ସବୁଠାରୁ ସାଇନ୍ ଆଉଟ୍ ହେବେ ଏବଂ ସାଇନ୍ ଇନ୍ କରିପାରିବେ ନାହିଁ। ସେମାନଙ୍କ ରେକର୍ଡ ରହିବ।',
    'access.reactivateNote': '{name} ପୁଣି ସାଇନ୍ ଇନ୍ କରିପାରିବେ।',
    'access.activity': 'କାର୍ଯ୍ୟକଳାପ',
    'access.noActivity': 'ଏପର୍ଯ୍ୟନ୍ତ କିଛି ରେକର୍ଡ ହୋଇନାହିଁ।',
    'access.by': '{name} ଦ୍ୱାରା',
    'access.onAnotherAccount': 'ଅନ୍ୟ ଖାତାରେ',
    'access.act.create': 'ଖାତା ସୃଷ୍ଟି ହେଲା',
    'access.act.update': 'ବିବରଣୀ ବଦଳିଲା',
    'access.act.role_change': 'ଭୂମିକା ବଦଳିଲା',
    'access.act.activate': 'ସକ୍ରିୟ କରାଗଲା',
    'access.act.deactivate': 'ନିଷ୍କ୍ରିୟ କରାଗଲା',
    'access.act.login': 'ସାଇନ୍ ଇନ୍ କଲେ',
    'access.act.logout': 'ସାଇନ୍ ଆଉଟ୍ କଲେ',
    'access.act.login_failed': 'ସାଇନ୍ ଇନ୍ ବିଫଳ',
    'access.act.otp_sent': 'ସାଇନ୍-ଇନ୍ କୋଡ୍ ପଠାଗଲା',
    'access.act.password_reset': 'ପାସୱାର୍ଡ ରିସେଟ୍',
  },
})
